import { useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { Select } from '@/components/Select';
import { PrivateScreenshot } from '@/components/funnel-audits/PrivateScreenshot';
import {
  BONUS_TYPES, KYC_POLICIES, PAYMENT_METHODS, REGISTRATION_FIELDS, SCREENSHOT_CATEGORIES,
  createFunnelAudit, createFunnelAuditBrand, getFunnelAudit, getFunnelAuditBrands,
  getFunnelAuditOffers, getFunnelAuditRate, getPublicFunnelAuditLink, getPublicFunnelAuditRate,
  submitPublicFunnelAudit, updateFunnelAudit, uploadFunnelAuditScreenshot,
  uploadPublicFunnelAuditScreenshot,
  type FunnelAuditInput, type FunnelAuditScreenshot, type PublicFunnelAuditInput,
  type ScreenshotCategory,
} from '@/api/funnel-audits';
import {
  BONUS_LABELS, KYC_LABELS, PAYMENT_METHOD_LABELS, REGISTRATION_FIELD_LABELS,
  SCREENSHOT_LABELS, formatMinor, offerLabel, parseMinor,
} from '@/lib/funnel-audits';
import { CURRENCY_OPTIONS } from '@/lib/currencies';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { useBranding } from '@/hooks/useBranding';

interface FormState {
  brand_name: string;
  brand_id: string;
  offer_id: string;
  offer_url: string;
  geos: string[];
  has_localization: boolean | null;
  reg_complexity_score: number | null;
  has_instant_one_click: boolean | null;
  registration_fields: string[];
  min_deposit: string;
  min_deposit_currency: string;
  payment_methods: string[];
  custom_payment_methods: string;
  has_crash_games: boolean | null;
  bonus_type: FunnelAuditInput['bonus_type'];
  kyc_policy: FunnelAuditInput['kyc_policy'];
  notes: string;
}

interface PendingScreenshot {
  id: string;
  category: ScreenshotCategory;
  file: File;
  previewUrl: string;
  status: 'uploading' | 'error';
  error?: string;
}

const emptyForm: FormState = {
  brand_name: '', brand_id: '', offer_id: '', offer_url: '', geos: [], has_localization: null,
  reg_complexity_score: null, has_instant_one_click: null, registration_fields: [],
  min_deposit: '', min_deposit_currency: 'USD', payment_methods: [], custom_payment_methods: '',
  has_crash_games: null, bonus_type: null, kyc_policy: null, notes: '',
};

const TRI_STATE_OPTIONS = [
  { value: '', label: 'Not checked' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

function TriState({ value, onChange }: { value: boolean | null; onChange: (value: boolean | null) => void }) {
  return (
    <Select
      value={value == null ? '' : value ? 'yes' : 'no'}
      onChange={(next) => onChange(next === '' ? null : next === 'yes')}
      options={TRI_STATE_OPTIONS}
      placeholder="Not checked"
      className="w-full"
    />
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="card space-y-4"><h2 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">{title}</h2>{children}</section>;
}

function Field({ label, required, children, error }: { label: string; required?: boolean; children: React.ReactNode; error?: string }) {
  return (
    <label className="block">
      <span className="label">{label}{required && <span className="text-danger"> *</span>}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-danger">{error}</span>}
    </label>
  );
}

export function FunnelAuditForm() {
  const { name: brand } = useBranding();
  const { id, token: publicToken } = useParams();
  const publicMode = Boolean(publicToken);
  const editing = Boolean(id);
  const adminCanEdit = useAuthStore((state) => state.canEdit());
  const canEdit = publicMode || adminCanEdit;
  const pushToast = useToastStore((state) => state.push);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initialized = useRef('');
  const pendingPreviewUrls = useRef(new Set<string>());
  const [form, setForm] = useState<FormState>(emptyForm);
  const [shots, setShots] = useState<FunnelAuditScreenshot[]>([]);
  const [pendingScreenshots, setPendingScreenshots] = useState<PendingScreenshot[]>([]);
  const [draggingCategory, setDraggingCategory] = useState<ScreenshotCategory | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [showNewBrand, setShowNewBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const publicLink = useQuery({
    queryKey: ['funnel-audit-public-link', publicToken],
    queryFn: () => getPublicFunnelAuditLink(publicToken!),
    enabled: publicMode,
    retry: false,
  });
  const brands = useQuery({
    queryKey: ['funnel-audit-brands'],
    queryFn: getFunnelAuditBrands,
    enabled: !publicMode,
  });
  const offers = useQuery({
    queryKey: ['funnel-audit-offers'],
    queryFn: getFunnelAuditOffers,
    enabled: !publicMode,
  });
  const detail = useQuery({
    queryKey: ['funnel-audit', id],
    queryFn: () => getFunnelAudit(id!),
    enabled: editing,
  });

  useEffect(() => {
    if (!detail.data || initialized.current === detail.data.audit.id) return;
    const audit = detail.data.audit;
    const knownPayments = new Set<string>(PAYMENT_METHODS);
    setForm({
      brand_name: '',
      brand_id: audit.brand_id,
      offer_id: audit.offer_id ?? '',
      offer_url: audit.offer_url,
      geos: audit.geos,
      has_localization: audit.has_localization == null ? null : Boolean(audit.has_localization),
      reg_complexity_score: audit.reg_complexity_score,
      has_instant_one_click: audit.has_instant_one_click == null ? null : Boolean(audit.has_instant_one_click),
      registration_fields: audit.registration_fields,
      min_deposit: formatMinor(audit.min_deposit_amount),
      min_deposit_currency: audit.min_deposit_currency,
      payment_methods: audit.payment_methods.filter((method) => knownPayments.has(method)),
      custom_payment_methods: audit.payment_methods.filter((method) => !knownPayments.has(method)).join(', '),
      has_crash_games: audit.has_crash_games == null ? null : Boolean(audit.has_crash_games),
      bonus_type: audit.bonus_type,
      kyc_policy: audit.kyc_policy,
      notes: audit.notes ?? '',
    });
    setShots(detail.data.screenshots);
    initialized.current = audit.id;
  }, [detail.data]);

  useEffect(() => () => {
    for (const previewUrl of pendingPreviewUrls.current) URL.revokeObjectURL(previewUrl);
    pendingPreviewUrls.current.clear();
  }, []);

  const selectedBrand = useMemo(
    () => (brands.data ?? []).find((brand) => brand.id === form.brand_id),
    [brands.data, form.brand_id],
  );
  const selectedOffer = useMemo(
    () => (offers.data ?? []).find((offer) => offer.id === form.offer_id),
    [offers.data, form.offer_id],
  );
  const selectedOfferGeos = useMemo(
    () => selectedOffer?.geo.split(',').map((geo) => geo.trim().toUpperCase()).filter(Boolean) ?? [],
    [selectedOffer],
  );
  const brandOptions = useMemo(() => [
    { value: '', label: 'Select brand…' },
    ...(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
  ], [brands.data]);
  const offerOptions = useMemo(() => {
    const options = (offers.data ?? []).filter((offer) => {
      if (!selectedBrand) return false;
      return (offer.brand?.trim() || offer.partner).toLowerCase() === selectedBrand.name_key;
    }).map((offer) => ({
      value: offer.id,
      label: `${offerLabel(offer)}${offer.status === 'paused' ? ' [paused]' : ''}`,
    }));
    if (editing && detail.data?.audit.offer_id && form.offer_id && !options.some((option) => option.value === form.offer_id)) {
      options.unshift({ value: form.offer_id, label: 'Deleted source offer — historical audit' });
    }
    return [{ value: '', label: 'No offer yet' }, ...options];
  }, [offers.data, selectedBrand, editing, detail.data?.audit.offer_id, form.offer_id]);
  const minor = parseMinor(form.min_deposit);
  const rate = useQuery({
    queryKey: ['funnel-audit-rate', publicToken ?? 'admin', form.min_deposit_currency],
    queryFn: () => publicToken
      ? getPublicFunnelAuditRate(publicToken, form.min_deposit_currency)
      : getFunnelAuditRate(form.min_deposit_currency),
    enabled: /^[A-Z]{3}$/.test(form.min_deposit_currency),
    retry: false,
  });

  const save = useMutation({
    mutationFn: async (input: FunnelAuditInput | PublicFunnelAuditInput) => {
      if ('brand_name' in input) {
        if (!publicToken) throw new Error('Public form token is missing');
        return submitPublicFunnelAudit(publicToken, input);
      }
      if (!editing) return createFunnelAudit(input);
      return updateFunnelAudit(id!, input);
    },
    onSuccess: async (result) => {
      if (publicMode) {
        setSubmitted(true);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['funnel-audits'] });
      const savedId = editing ? id! : 'id' in result ? result.id : '';
      pushToast(editing ? 'Funnel audit updated successfully.' : 'Funnel audit saved successfully.');
      navigate(`/funnel-audits/${savedId}`);
    },
    onError: (error: Error) => setSubmitError(error.message || 'Failed to save audit'),
  });

  const createBrand = useMutation({
    mutationFn: () => createFunnelAuditBrand(newBrandName),
    onSuccess: async (brand) => {
      await queryClient.invalidateQueries({ queryKey: ['funnel-audit-brands'] });
      setForm((current) => ({ ...current, brand_id: brand.id, offer_id: '' }));
      setNewBrandName('');
      setShowNewBrand(false);
      setErrors((current) => ({ ...current, brand_id: '' }));
      pushToast(`Brand ${brand.name} created.`);
    },
    onError: (error: Error) => setSubmitError(error.message || 'Failed to create brand'),
  });

  function change<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: '' }));
  }

  function chooseOffer(offerId: string) {
    const offer = (offers.data ?? []).find((candidate) => candidate.id === offerId);
    setForm((current) => ({
      ...current,
      offer_id: offerId,
      offer_url: offer?.offer_url ?? current.offer_url,
      geos: offer
        ? offer.geo.split(',').map((geo) => geo.trim().toUpperCase()).filter(Boolean)
        : current.geos,
    }));
    setErrors((current) => ({ ...current, offer_id: '', offer_url: '', geos: '' }));
  }

  function chooseBrand(brandId: string) {
    setForm((current) => ({ ...current, brand_id: brandId, offer_id: '' }));
    setErrors((current) => ({ ...current, brand_id: '', offer_id: '' }));
  }

  function toggleArray(key: 'registration_fields' | 'payment_methods', value: string) {
    const values = form[key];
    change(key, values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  }

  function removePendingScreenshot(id: string, knownPreviewUrl?: string) {
    const previewUrl = knownPreviewUrl
      ?? pendingScreenshots.find((item) => item.id === id)?.previewUrl;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      pendingPreviewUrls.current.delete(previewUrl);
    }
    setPendingScreenshots((current) => current.filter((item) => item.id !== id));
  }

  async function uploadPendingScreenshot(pending: PendingScreenshot) {
    setPendingScreenshots((current) => current.map((item) => (
      item.id === pending.id ? { ...item, status: 'uploading', error: undefined } : item
    )));
    try {
      if (pending.file.size > 10 * 1024 * 1024) throw new Error('File exceeds 10 MB');
      const shot = publicToken
        ? await uploadPublicFunnelAuditScreenshot(publicToken, pending.file, pending.category)
        : await uploadFunnelAuditScreenshot(pending.file, pending.category);
      setShots((current) => [...current, shot]);
      removePendingScreenshot(pending.id, pending.previewUrl);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      setPendingScreenshots((current) => current.map((item) => (
        item.id === pending.id ? { ...item, status: 'error', error: message } : item
      )));
    }
  }

  function upload(category: ScreenshotCategory, files: FileList | null) {
    if (!files?.length) return;
    setSubmitError('');
    const pending = Array.from(files).map<PendingScreenshot>((file) => {
      const previewUrl = URL.createObjectURL(file);
      pendingPreviewUrls.current.add(previewUrl);
      return {
        id: crypto.randomUUID(),
        category,
        file,
        previewUrl,
        status: 'uploading',
      };
    });
    setPendingScreenshots((current) => [...current, ...pending]);
    for (const item of pending) void uploadPendingScreenshot(item);
  }

  function dropScreenshots(category: ScreenshotCategory, event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    event.stopPropagation();
    setDraggingCategory(null);
    upload(category, event.dataTransfer.files);
  }

  function retryPendingScreenshot(id: string) {
    const pending = pendingScreenshots.find((item) => item.id === id);
    if (pending) void uploadPendingScreenshot(pending);
  }

  function pendingTile(pending: PendingScreenshot) {
    return (
      <div key={pending.id} className="relative h-28 overflow-hidden rounded-md border border-border bg-surface-muted">
        <img src={pending.previewUrl} alt={pending.file.name} className="h-full w-full object-cover" />
        <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center text-white ${pending.status === 'error' ? 'bg-danger/85' : 'bg-black/55'}`}>
          {pending.status === 'uploading' ? (
            <>
              <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/35 border-t-white" aria-hidden />
              <span className="max-w-full truncate text-xs font-medium">Uploading {pending.file.name}…</span>
            </>
          ) : (
            <>
              <span className="line-clamp-2 text-xs font-medium">{pending.error || 'Upload failed'}</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => retryPendingScreenshot(pending.id)} className="rounded bg-white px-2 py-1 text-xs font-medium text-danger">Retry</button>
                <button type="button" onClick={() => removePendingScreenshot(pending.id)} className="rounded border border-white/70 px-2 py-1 text-xs font-medium text-white">Remove</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function validate(): FunnelAuditInput | PublicFunnelAuditInput | null {
    const found: Record<string, string> = {};
    if (publicMode) {
      if (!form.brand_name.trim()) found.brand_name = 'Enter the brand name';
    } else if (!form.brand_id) {
      found.brand_id = 'Select or create a brand';
    }
    if (form.geos.length === 0) found.geos = 'Select at least one GEO';
    try { new URL(form.offer_url); } catch { found.offer_url = 'Enter a complete URL including https://'; }
    const amount = parseMinor(form.min_deposit);
    if (amount == null) found.min_deposit = 'Enter a valid amount with up to two decimals';
    if (!/^[A-Z]{3}$/.test(form.min_deposit_currency)) found.min_deposit_currency = 'Use a 3-letter currency code';
    setErrors(found);
    if (Object.keys(found).length > 0 || amount == null) return null;
    const custom = form.custom_payment_methods.split(',').map((value) => value.trim()).filter(Boolean);
    const common = {
      offer_url: form.offer_url,
      geos: form.geos,
      has_localization: form.has_localization,
      reg_complexity_score: form.reg_complexity_score,
      has_instant_one_click: form.has_instant_one_click,
      registration_fields: form.registration_fields,
      min_deposit_amount: amount,
      min_deposit_currency: form.min_deposit_currency,
      payment_methods: Array.from(new Set([...form.payment_methods, ...custom])),
      has_crash_games: form.has_crash_games,
      bonus_type: form.bonus_type,
      kyc_policy: form.kyc_policy,
      notes: form.notes.trim() || null,
      screenshot_ids: shots.map((shot) => shot.id),
    };
    if (publicMode) return { ...common, brand_name: form.brand_name.trim() };
    return { ...common, brand_id: form.brand_id, offer_id: form.offer_id || null };
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canEdit || save.isPending) return;
    if (pendingScreenshots.length > 0) {
      setSubmitError('Wait for screenshot uploads to finish or remove failed files.');
      return;
    }
    setSubmitError('');
    const input = validate();
    if (input) save.mutate(input);
  }

  if (editing && detail.isLoading) return <div className="py-16 text-center text-content-subtle">Loading audit…</div>;
  if (editing && detail.isError) return <div className="card text-danger">Failed to load audit.</div>;
  if (publicMode && publicLink.isLoading) return <div className="py-16 text-center text-content-subtle">Loading form…</div>;
  if (publicMode && publicLink.isError) return <div className="mx-auto mt-16 max-w-lg card text-center text-danger">This form link is invalid or inactive. Ask your {brand} manager for a new link.</div>;
  if (publicMode && submitted) {
    return (
      <div className="mx-auto mt-16 max-w-lg card text-center">
        <h1 className="text-xl font-semibold text-content">Thank you</h1>
        <p className="mt-2 text-sm text-content-muted">Your brand and funnel details were submitted successfully to {publicLink.data?.owner_name}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mx-auto max-w-4xl space-y-5">
      <div className="flex items-center gap-3">
        <div><h1 className="text-xl font-semibold text-content">{editing ? 'Edit funnel audit' : publicMode ? 'Brand funnel registration' : 'New funnel audit'}</h1><p className="mt-1 text-sm text-content-subtle">{publicMode ? `Submit the details for your new brand to ${publicLink.data?.owner_name}.` : 'Audit a brand now and attach an offer whenever it is ready.'}</p></div>
        {!publicMode && <Button type="button" variant="secondary" className="ml-auto" onClick={() => navigate(editing ? `/funnel-audits/${id}` : '/funnel-audits')}>Cancel</Button>}
      </div>

      {submitError && <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">{submitError}</div>}

      <Section title="Brand and funnel">
        {publicMode ? (
          <Field label="New brand name" required error={errors.brand_name}>
            <input className="input" value={form.brand_name} onChange={(event) => change('brand_name', event.target.value)} maxLength={120} placeholder="Brand name" />
          </Field>
        ) : <>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <Field label="Brand" required error={errors.brand_id}>
            <Select value={form.brand_id} onChange={chooseBrand} options={brandOptions} placeholder="Select brand…" searchable className="w-full" />
          </Field>
          <Button type="button" variant="secondary" onClick={() => setShowNewBrand((value) => !value)}>New brand</Button>
        </div>
        {showNewBrand && (
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-muted p-3 sm:flex-row sm:items-end">
            <Field label="New brand name">
              <input className="input" value={newBrandName} onChange={(event) => setNewBrandName(event.target.value)} maxLength={120} placeholder="Brand name" />
            </Field>
            <Button type="button" loading={createBrand.isPending} disabled={!newBrandName.trim()} onClick={() => createBrand.mutate()}>Create brand</Button>
          </div>
        )}
        <Field label="Existing offer (optional)">
          <Select value={form.offer_id} onChange={chooseOffer} options={offerOptions} placeholder="No offer yet" searchable disabled={!form.brand_id} className="w-full" />
        </Field>
        </>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Funnel URL" required error={errors.offer_url}><input className="input" value={form.offer_url} onChange={(event) => change('offer_url', event.target.value)} placeholder="https://" /></Field>
          <Field label="GEOs" required error={errors.geos}>
            <CountryMultiSelect
              value={form.geos}
              onChange={(value) => change('geos', value)}
              restrictTo={selectedOffer ? selectedOfferGeos : undefined}
              placeholder="Search and select countries…"
              emptyHint={selectedOffer ? 'Select one or more GEOs supported by this offer.' : 'Select one or more GEOs for this brand audit.'}
            />
          </Field>
        </div>
      </Section>

      <Section title="Localization and registration">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Localized experience"><TriState value={form.has_localization} onChange={(value) => change('has_localization', value)} /></Field>
          <Field label="Registration complexity">
            <Select
              value={form.reg_complexity_score?.toString() ?? ''}
              onChange={(value) => change('reg_complexity_score', value ? Number(value) : null)}
              options={[
                { value: '', label: 'Not checked' },
                ...[1, 2, 3, 4, 5].map((score) => ({ value: String(score), label: `${score} / 5` })),
              ]}
              placeholder="Not checked"
              className="w-full"
            />
          </Field>
          <Field label="Instant / one-click registration"><TriState value={form.has_instant_one_click} onChange={(value) => change('has_instant_one_click', value)} /></Field>
        </div>
        <Field label="Required registration fields">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {REGISTRATION_FIELDS.map((field) => <label key={field} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-content-muted"><input type="checkbox" checked={form.registration_fields.includes(field)} onChange={() => toggleArray('registration_fields', field)} />{REGISTRATION_FIELD_LABELS[field]}</label>)}
          </div>
        </Field>
      </Section>

      <Section title="Finance">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Minimum deposit" required error={errors.min_deposit}><input className="input" inputMode="decimal" value={form.min_deposit} onChange={(event) => change('min_deposit', event.target.value)} placeholder="0.00" /></Field>
          <Field label="Currency" required error={errors.min_deposit_currency}>
            <Select
              value={form.min_deposit_currency}
              onChange={(value) => change('min_deposit_currency', value)}
              options={CURRENCY_OPTIONS}
              placeholder="Select currency…"
              searchable
              className="w-full"
            />
          </Field>
        </div>
        {minor != null && rate.data && <p className="text-sm text-content-subtle">≈ ${(minor * rate.data.rate / 100).toFixed(2)} USD at {rate.data.rate.toFixed(6)} USD/{rate.data.currency}</p>}
        {rate.isError && <p className="text-sm text-danger">Rate unavailable for this currency.</p>}
        <Field label="Payment methods">
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {PAYMENT_METHODS.map((method) => <label key={method} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-content-muted"><input type="checkbox" checked={form.payment_methods.includes(method)} onChange={() => toggleArray('payment_methods', method)} />{PAYMENT_METHOD_LABELS[method]}</label>)}
          </div>
        </Field>
        <Field label="Other payment methods (comma-separated)"><input className="input" value={form.custom_payment_methods} onChange={(event) => change('custom_payment_methods', event.target.value)} placeholder="boleto, paytm" /></Field>
      </Section>

      <Section title="Product">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Crash games"><TriState value={form.has_crash_games} onChange={(value) => change('has_crash_games', value)} /></Field>
          <Field label="Welcome bonus">
            <Select
              value={form.bonus_type ?? ''}
              onChange={(value) => change('bonus_type', value ? value as FunnelAuditInput['bonus_type'] : null)}
              options={[{ value: '', label: 'Not checked' }, ...BONUS_TYPES.map((type) => ({ value: type, label: BONUS_LABELS[type] }))]}
              placeholder="Not checked"
              className="w-full"
            />
          </Field>
          <Field label="KYC policy">
            <Select
              value={form.kyc_policy ?? ''}
              onChange={(value) => change('kyc_policy', value ? value as FunnelAuditInput['kyc_policy'] : null)}
              options={[{ value: '', label: 'Not checked' }, ...KYC_POLICIES.map((policy) => ({ value: policy, label: KYC_LABELS[policy] }))]}
              placeholder="Not checked"
              className="w-full"
            />
          </Field>
        </div>
      </Section>

      <Section title="Screenshots">
        <div className="grid gap-4 sm:grid-cols-2">
          {SCREENSHOT_CATEGORIES.map((category) => {
            const categoryShots = shots.filter((shot) => shot.category === category);
            const categoryPending = pendingScreenshots.filter((pending) => pending.category === category);
            const categoryUploading = categoryPending.some((pending) => pending.status === 'uploading');
            return (
              <div key={category} className="rounded-lg border border-border p-3">
                <div className="mb-3 text-sm font-medium text-content">{SCREENSHOT_LABELS[category]}</div>
                <label
                  onDragEnter={(event) => {
                    event.preventDefault();
                    if (!categoryUploading) setDraggingCategory(category);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                    if (!categoryUploading) setDraggingCategory(category);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    if (draggingCategory === category) setDraggingCategory(null);
                  }}
                  onDrop={(event) => {
                    if (categoryUploading) {
                      event.preventDefault();
                      return;
                    }
                    dropScreenshots(category, event);
                  }}
                  className={`mb-3 flex min-h-20 flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-3 text-center transition-colors ${
                    draggingCategory === category
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border-strong text-content-subtle hover:border-primary/60'
                  } ${categoryUploading ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    className="hidden"
                    disabled={categoryUploading}
                    onChange={(event) => { upload(category, event.target.files); event.target.value = ''; }}
                  />
                  {categoryUploading ? (
                    <span className="text-sm">Uploading…</span>
                  ) : (
                    <>
                      <span className="text-sm font-medium text-content-muted">Drop images here or click to browse</span>
                      <span className="mt-1 text-xs">PNG, JPG or WEBP · max 10 MB each</span>
                    </>
                  )}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {categoryShots.map((shot) => (
                    <div key={shot.id} className="group relative">
                      <PrivateScreenshot id={shot.id} alt={shot.original_name} publicToken={publicToken} className="h-28 w-full rounded-md border border-border object-cover" />
                      <button type="button" onClick={() => setShots((current) => current.filter((item) => item.id !== shot.id))} className="absolute right-1 top-1 rounded bg-black/70 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100">Remove</button>
                    </div>
                  ))}
                  {categoryPending.map(pendingTile)}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Notes"><textarea className="input min-h-28" value={form.notes} onChange={(event) => change('notes', event.target.value)} maxLength={4000} placeholder="Loading speed, blockers, unusual steps, observations…" /></Section>

      <div className="flex items-center gap-3 pb-8"><Button type="submit" loading={save.isPending} disabled={!canEdit || pendingScreenshots.length > 0}>{editing ? 'Save changes' : publicMode ? 'Submit brand' : 'Create audit'}</Button>{!canEdit && <span className="text-sm text-warning">Your account is read-only.</span>}{canEdit && pendingScreenshots.length > 0 && <span className="text-sm text-content-subtle">Finish screenshot uploads before saving.</span>}</div>
    </form>
  );
}
